import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Receipt, Building2, Download, FileText, FileSpreadsheet, ChevronDown, ChevronRight,
  Filter, RotateCcw, Lock, LockOpen, CheckCircle2, ShieldCheck, AlertTriangle,
} from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const thisMonth = () => new Date().toISOString().slice(0, 7);
const dateFmt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

export default function SmartBillingSection({ branchId }) {
  const { user } = useAuth();
  const canLock = user && ["company_admin", "super_admin"].includes(user.role);

  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const [mode, setMode] = useState("month"); // 'month' | 'range'
  const [filters, setFilters] = useState({
    month: thisMonth(), from_dt: first, to_dt: "", company_id: "",
  });
  const [companies, setCompanies] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyFmt, setBusyFmt] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busyRow, setBusyRow] = useState(null); // key = `${co}::${cc}`
  const [lockDlg, setLockDlg] = useState(false);
  const [lockNote, setLockNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { branch_id: branchId || undefined };
      if (mode === "month" && filters.month) {
        params.month = filters.month;
      } else {
        if (filters.from_dt) params.from_dt = filters.from_dt;
        if (filters.to_dt) params.to_dt = filters.to_dt;
      }
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.courier) params.courier = filters.courier;
      const { data: d } = await api.get("/reports/smart-billing", { params });
      setData(d);
      const exp = {};
      (d.by_company || []).forEach((c) => { exp[c.company_id || "unassigned"] = true; });
      setExpanded(exp);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Smart Billing report");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/companies", { params: { active: true } })
      .then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/couriers").then((r) => setCouriers((r.data || []).map((c) => c.name || c).filter(Boolean))).catch(() => {});
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const rowsByCompany = useMemo(() => {
    const map = {};
    (data?.rows || []).forEach((r) => {
      const k = r.company_id || "unassigned";
      (map[k] = map[k] || []).push(r);
    });
    return map;
  }, [data]);

  const verifiedMap = useMemo(() => {
    const m = {};
    (data?.reconciliation?.verifications || []).forEach((v) => {
      const k = `${v.company_id || "null"}::${v.cost_center_id || "null"}`;
      m[k] = v;
    });
    return m;
  }, [data]);

  const reconciliation = data?.reconciliation || null;
  const locked = !!reconciliation?.locked;
  const isMonthMode = mode === "month" && !!filters.month;

  const resetFilters = () => {
    setMode("month");
    setFilters({ month: thisMonth(), from_dt: first, to_dt: "", company_id: "", courier: "" });
    setTimeout(load, 0);
  };

  const download = async (format) => {
    setBusyFmt(format);
    try {
      const params = { format, branch_id: branchId || undefined };
      if (mode === "month" && filters.month) params.month = filters.month;
      else {
        if (filters.from_dt) params.from_dt = filters.from_dt;
        if (filters.to_dt) params.to_dt = filters.to_dt;
      }
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.courier) params.courier = filters.courier;
      const { data: blob } = await api.get("/reports/smart-billing/export",
        { params, responseType: "blob" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smart-billing-${filters.month || filters.from_dt || "all"}.${format === "excel" ? "xlsx" : format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${format.toUpperCase()} download failed`);
    } finally { setBusyFmt(null); }
  };

  const toggleVerify = async (row, next) => {
    if (locked) return toast.error("Month is locked. Unlock to make changes.");
    if (!isMonthMode) return toast.error("Select a Month to verify rows.");
    const rowKey = `${row.company_id || "null"}::${row.cost_center_id || "null"}`;
    setBusyRow(rowKey);
    try {
      await api.post("/reports/smart-billing/verify", {
        month: filters.month, company_id: row.company_id,
        cost_center_id: row.cost_center_id, verified: next,
      });
      toast.success(next ? "Row verified" : "Verification removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verify failed");
    } finally { setBusyRow(null); }
  };

  const lockMonth = async () => {
    try {
      await api.post("/reports/smart-billing/lock",
        { month: filters.month, note: lockNote || "" });
      toast.success(`Month ${filters.month} is now locked.`);
      setLockDlg(false); setLockNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lock failed");
    }
  };

  const unlockMonth = async () => {
    if (!window.confirm(`Unlock ${filters.month}? Verifications become editable again.`)) return;
    try {
      await api.delete(`/reports/smart-billing/lock/${filters.month}`);
      toast.success(`Month ${filters.month} unlocked.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    }
  };

  const grand = data?.grand_total || {};
  const verifiedCount = (data?.reconciliation?.verifications || []).length;
  const totalRows = (data?.rows || []).length;

  return (
    <section className="border border-border bg-card rounded-md" data-testid="smart-billing-section">
      <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1.5"><Receipt size={12} className="text-primary"/> Vendor bill authentication</div>
          <div className="font-display text-xl mt-1 flex items-center gap-2">
            Smart Billing Report
            {locked && (
              <span data-testid="sb-locked-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border border-primary/40 bg-red-50 text-primary rounded px-2 py-0.5">
                <Lock size={10}/> Locked
              </span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            Grouped by <b>Company</b> (legal entity) → <b>Cost Center</b> · verify each row and lock the month once authenticated.
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="smart-billing-exports">
          {isMonthMode && canLock && !locked && (
            <Button data-testid="sb-lock-btn" onClick={() => setLockDlg(true)}
              disabled={loading || verifiedCount < totalRows || totalRows === 0}
              variant="outline" size="sm"
              title={verifiedCount < totalRows ? "Verify every row before locking" : ""}
              className="rounded-md border-primary/50 text-primary hover:bg-primary hover:text-white disabled:opacity-40">
              <Lock size={12} className="mr-1.5"/> Lock month
            </Button>
          )}
          {isMonthMode && canLock && locked && (
            <Button data-testid="sb-unlock-btn" onClick={unlockMonth}
              variant="outline" size="sm"
              className="rounded-md border-emerald-500 text-emerald-700 hover:bg-emerald-600 hover:text-white">
              <LockOpen size={12} className="mr-1.5"/> Unlock
            </Button>
          )}
          <button data-testid="sb-export-csv" disabled={!!busyFmt} onClick={() => download("csv")}
            className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card disabled:opacity-50">
            <FileText size={12} /> {busyFmt === "csv" ? "…" : "CSV"}
          </button>
          <button data-testid="sb-export-xlsx" disabled={!!busyFmt} onClick={() => download("xlsx")}
            className="inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card disabled:opacity-50">
            <FileSpreadsheet size={12} /> {busyFmt === "xlsx" ? "…" : "Excel"}
          </button>
          <button data-testid="sb-export-pdf" disabled={!!busyFmt} onClick={() => download("pdf")}
            className="inline-flex items-center gap-1.5 border border-primary/40 bg-primary/5 text-primary rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-primary hover:text-white transition-colors disabled:opacity-50">
            <Download size={12} /> {busyFmt === "pdf" ? "…" : "PDF"}
          </button>
        </div>
      </div>

      {/* Lock banner */}
      {locked && (
        <div className="mx-5 mt-4 border border-primary/40 bg-red-50/60 rounded-md p-3 flex items-center gap-3 text-xs" data-testid="sb-lock-banner">
          <ShieldCheck size={18} className="text-primary shrink-0"/>
          <div className="flex-1">
            <div className="font-bold text-primary uppercase tracking-widest">Month locked · {reconciliation?.month}</div>
            <div className="text-muted-foreground mt-0.5">
              Locked by <b>{reconciliation?.lock_meta?.locked_by_name || "—"}</b> on {dateFmt(reconciliation?.lock_meta?.locked_at)}
              {reconciliation?.lock_meta?.note ? ` · "${reconciliation.lock_meta.note}"` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Mode toggle + filters */}
      <div className="p-5 border-b border-border space-y-3" data-testid="smart-billing-filters">
        <div className="inline-flex border border-border rounded-md overflow-hidden text-xs">
          <button data-testid="sb-mode-month" onClick={() => setMode("month")}
            className={`px-3 py-1.5 uppercase tracking-widest font-semibold ${mode === "month" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            Monthly (reconciliation)
          </button>
          <button data-testid="sb-mode-range" onClick={() => setMode("range")}
            className={`px-3 py-1.5 uppercase tracking-widest font-semibold ${mode === "range" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
            Custom range
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          {mode === "month" ? (
            <label className="block">
              <span className="overline">Month (YYYY-MM)</span>
              <Input data-testid="sb-month" type="month" value={filters.month}
                onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))}
                className="mt-1 rounded-md h-9 mono text-xs"/>
            </label>
          ) : (
            <>
              <label className="block">
                <span className="overline">From date</span>
                <Input data-testid="sb-from" type="date" value={filters.from_dt}
                  onChange={(e) => setFilters((f) => ({ ...f, from_dt: e.target.value }))}
                  className="mt-1 rounded-md h-9 mono text-xs"/>
              </label>
              <label className="block">
                <span className="overline">To date (optional)</span>
                <Input data-testid="sb-to" type="date" value={filters.to_dt}
                  onChange={(e) => setFilters((f) => ({ ...f, to_dt: e.target.value }))}
                  className="mt-1 rounded-md h-9 mono text-xs"/>
              </label>
            </>
          )}
          <label className="block">
            <span className="overline flex items-center gap-1"><Building2 size={11}/> Company (legal entity)</span>
            <select data-testid="sb-company" value={filters.company_id}
              onChange={(e) => setFilters((f) => ({ ...f, company_id: e.target.value }))}
              className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-xs mono">
              <option value="">All companies</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="overline">Courier</span>
            <select data-testid="sb-courier" value={filters.courier}
              onChange={(e) => setFilters((f) => ({ ...f, courier: e.target.value }))}
              className="mt-1 w-full h-9 px-3 rounded-md border border-input bg-background text-xs mono">
              <option value="">All couriers</option>
              {couriers.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <div className="flex gap-2">
            <Button data-testid="sb-apply" onClick={load} disabled={loading}
              className="rounded-md bg-primary text-primary-foreground hover-brut shadow-brut flex-1">
              <Filter size={13} className="mr-1.5"/> {loading ? "Loading…" : "Apply"}
            </Button>
            <Button data-testid="sb-reset" onClick={resetFilters} disabled={loading}
              variant="outline" className="rounded-md">
              <RotateCcw size={13}/>
            </Button>
          </div>
        </div>
      </div>

      {/* Totals + verify progress */}
      <div className="p-5 border-b border-border grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sb-totals">
        <TotalCard label="Parcels" value={num(grand.parcels)} />
        <TotalCard label="Weight (kg)" value={num(grand.weight_kg)} />
        <TotalCard label="Net" value={inr(grand.net_amount)} tone="text-slate-800"/>
        <TotalCard label="GST" value={inr(grand.gst_amount)} tone="text-amber-700"/>
        <TotalCard label="Gross" value={inr(grand.gross_amount)} tone="text-primary" bold/>
      </div>
      {isMonthMode && totalRows > 0 && (
        <div className="px-5 pt-3 pb-1 text-xs flex items-center gap-2" data-testid="sb-verify-progress">
          <CheckCircle2 size={13} className={verifiedCount === totalRows ? "text-emerald-600" : "text-muted-foreground"}/>
          <span className="font-semibold">{verifiedCount}/{totalRows}</span> rows verified for {filters.month}
          {verifiedCount === totalRows && !locked && (
            <span className="text-emerald-700 font-semibold">· ready to lock</span>
          )}
        </div>
      )}

      {/* Grouped tables */}
      <div className="p-5 space-y-4" data-testid="sb-groups">
        {(data?.by_company || []).length === 0 && !loading && (
          <div className="text-center text-muted-foreground py-8 text-sm">
            No outward parcels in this window.
          </div>
        )}
        {(data?.by_company || []).map((c) => {
          const cid = c.company_id || "unassigned";
          const isOpen = !!expanded[cid];
          const rows = rowsByCompany[cid] || [];
          const targetCompany = c.company_id || null;
          const verifiedInCompany = rows.filter((r) => {
            const k = `${r.company_id || "null"}::${r.cost_center_id || "null"}`;
            return !!verifiedMap[k];
          }).length;
          const allChecked = rows.length > 0 && verifiedInCompany === rows.length;
          const someChecked = verifiedInCompany > 0 && verifiedInCompany < rows.length;
          const bulkBusy = busyRow === `bulk::${cid}`;
          const toggleAll = async (e) => {
            e.stopPropagation();
            if (locked) return toast.error("Month is locked. Unlock to make changes.");
            if (!isMonthMode) return toast.error("Select a Month to verify rows.");
            const next = !allChecked;
            setBusyRow(`bulk::${cid}`);
            try {
              await api.post("/reports/smart-billing/verify-bulk", {
                month: filters.month, company_id: targetCompany, verified: next,
              });
              toast.success(next ? `Verified ${rows.length} row${rows.length === 1 ? "" : "s"}` : "Verifications cleared");
              await load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Bulk verify failed");
            } finally { setBusyRow(null); }
          };
          return (
            <div key={cid} className="border border-border rounded-md" data-testid={`sb-company-${cid}`}>
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [cid]: !e[cid] }))}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/40 transition-colors"
                data-testid={`sb-toggle-${cid}`}
              >
                {isOpen ? <ChevronDown size={16} className="text-primary"/> : <ChevronRight size={16}/>}
                <Building2 size={16} className="text-primary"/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-base truncate">{c.company_name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {c.cost_center_count} cost centre{c.cost_center_count === 1 ? "" : "s"} · {num(c.parcels)} parcels · {num(c.weight_kg)} kg
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Gross</div>
                  <div className="font-bold text-primary mono text-lg" data-testid={`sb-company-gross-${cid}`}>{inr(c.gross_amount)}</div>
                  <div className="text-[10px] text-muted-foreground mono">Net {inr(c.net_amount)} + GST {inr(c.gst_amount)}</div>
                </div>
              </button>
              {isOpen && (
                <div className="overflow-x-auto border-t border-border">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-border text-left overline bg-muted/40">
                        {isMonthMode && (
                          <th className="p-3 w-10 text-center">
                            <label className="inline-flex items-center justify-center cursor-pointer" title={allChecked ? "Clear all verifications in this company" : "Verify all rows in this company"}>
                              <input
                                type="checkbox"
                                data-testid={`sb-verify-all-${cid}`}
                                checked={allChecked}
                                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                disabled={locked || bulkBusy || rows.length === 0}
                                onChange={toggleAll}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 accent-emerald-600 disabled:opacity-40"
                              />
                            </label>
                          </th>
                        )}
                        <th className="p-3">Cost Center</th>
                        <th className="p-3">Department</th>
                        <th className="p-3 text-right">Parcels</th>
                        <th className="p-3 text-right">Weight</th>
                        <th className="p-3 text-right">Net (₹)</th>
                        <th className="p-3 text-right">GST (₹)</th>
                        <th className="p-3 text-right">Gross (₹)</th>
                        <th className="p-3">Couriers</th>
                        <th className="p-3">Used By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const key = `${r.company_id || "null"}::${r.cost_center_id || "null"}`;
                        const vf = verifiedMap[key];
                        const isVerified = !!vf;
                        const rowBusy = busyRow === key;
                        return (
                          <tr key={i}
                              className={`border-b border-border hover:bg-muted/30 ${isVerified ? "bg-emerald-50/40" : ""}`}
                              data-testid={`sb-row-${cid}-${i}`}>
                            {isMonthMode && (
                              <td className="p-3 text-center">
                                <label className="inline-flex items-center justify-center cursor-pointer" title={isVerified ? `Verified by ${vf.verified_by_name} at ${dateFmt(vf.verified_at)}` : "Mark as verified"}>
                                  <input
                                    type="checkbox"
                                    data-testid={`sb-verify-${cid}-${i}`}
                                    checked={isVerified}
                                    disabled={locked || rowBusy}
                                    onChange={(e) => toggleVerify(r, e.target.checked)}
                                    className="h-4 w-4 accent-emerald-600 disabled:opacity-40"
                                  />
                                </label>
                              </td>
                            )}
                            <td className="p-3 font-semibold">
                              {r.cost_center_name}
                              {isVerified && (
                                <div className="text-[9px] text-emerald-700 mono mt-0.5 flex items-center gap-1">
                                  <CheckCircle2 size={9}/> Verified by {vf.verified_by_name} · {dateFmt(vf.verified_at)}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-muted-foreground">{r.department}</td>
                            <td className="p-3 mono text-right">{num(r.parcels)}</td>
                            <td className="p-3 mono text-right">{num(r.weight_kg)}</td>
                            <td className="p-3 mono text-right">{inr(r.net_amount)}</td>
                            <td className="p-3 mono text-right text-amber-700">{inr(r.gst_amount)}</td>
                            <td className="p-3 mono text-right font-bold text-primary">{inr(r.gross_amount)}</td>
                            <td className="p-3 text-[10px] text-muted-foreground">{r.couriers.join(", ") || "—"}</td>
                            <td className="p-3 text-[10px] text-muted-foreground" data-testid={`sb-usedby-${cid}-${i}`}>{(r.used_by || []).join(", ") || "—"}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-primary/5 font-bold" data-testid={`sb-subtotal-${cid}`}>
                        {isMonthMode && <td></td>}
                        <td colSpan={2} className="p-3 text-primary">Company sub-total</td>
                        <td className="p-3 mono text-right">{num(c.parcels)}</td>
                        <td className="p-3 mono text-right">{num(c.weight_kg)}</td>
                        <td className="p-3 mono text-right">{inr(c.net_amount)}</td>
                        <td className="p-3 mono text-right text-amber-700">{inr(c.gst_amount)}</td>
                        <td className="p-3 mono text-right text-primary">{inr(c.gross_amount)}</td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lock confirmation dialog */}
      <Dialog open={lockDlg} onOpenChange={setLockDlg}>
        <DialogContent className="rounded-md max-w-md" data-testid="sb-lock-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Lock size={18} className="text-primary"/> Lock {filters.month}?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-2 border border-amber-500/40 bg-amber-50/50 rounded-md p-3 text-xs">
              <AlertTriangle size={14} className="text-amber-700 mt-0.5"/>
              <div>
                Once locked, verifications for <b>{filters.month}</b> cannot be added, removed, or changed. Only Company/Platform admins can unlock later.
              </div>
            </div>
            <label className="block">
              <span className="overline">Note (optional)</span>
              <Textarea data-testid="sb-lock-note" rows={2} value={lockNote}
                onChange={(e) => setLockNote(e.target.value)}
                placeholder="e.g. Bill authenticated by Finance on 12-Feb-2026"
                className="mt-1 rounded-md"/>
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setLockDlg(false)} className="rounded-md" data-testid="sb-lock-cancel">
                Cancel
              </Button>
              <Button onClick={lockMonth} className="rounded-md bg-primary text-primary-foreground" data-testid="sb-lock-confirm">
                <Lock size={13} className="mr-1"/> Lock month
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function TotalCard({ label, value, tone = "text-slate-800", bold = false }) {
  return (
    <div className="border border-border rounded-md bg-background p-3">
      <div className="overline text-[10px]">{label}</div>
      <div className={`mono mt-1 leading-none ${tone} ${bold ? "text-2xl font-bold" : "text-xl"}`}>
        {value}
      </div>
    </div>
  );
}
